import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Sparkles,
  Send,
  Loader2,
  MessageCircle,
  Briefcase,
  FileText,
  BookOpen,
  RotateCcw,
  Cpu,
  Download,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { AI_FEATURES } from "../../data/profile";
import { buildSystemPrompt } from "../../data/prompts";
import { useLocalAI } from "../../hooks/useLocalAI";

const FEATURE_ICONS = {
  persona_chat: MessageCircle,
  project_recommender: Briefcase,
  cover_letter: FileText,
  resume_qa: BookOpen,
};

const SUGGESTIONS = {
  persona_chat: [
    "Who are you?",
    "Tell me about your AI projects.",
    "What's your strongest backend stack?",
  ],
  project_recommender: [
    "We need a real-time CV system on AWS.",
    "Looking for someone to build a financial AI chatbot.",
    "Hiring a senior Django + DRF engineer.",
  ],
  cover_letter: [
    "Senior Python Engineer at a fintech startup. Stack: Django, PostgreSQL, AWS, Stripe.",
    "ML Engineer focused on computer vision pipelines and YOLO.",
  ],
  resume_qa: [
    "Which cloud services has Shivmurat used?",
    "What certifications does he hold?",
    "Has he worked with computer vision in production?",
  ],
};

export default function AISection() {
  const [activeId, setActiveId] = useState("persona_chat");
  const [chats, setChats] = useState({
    persona_chat: [],
    project_recommender: [],
    cover_letter: [],
    resume_qa: [],
  });
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  const { status, progress, device, error, streamingText, load, generate } =
    useLocalAI();

  const active = AI_FEATURES.find((f) => f.id === activeId);
  const messages = chats[activeId];
  const isLoading = status === "loading";
  const isGenerating = status === "generating";
  const isReady = status === "ready";

  // Display the live token-by-token stream as the last assistant message
  const displayedMessages = useMemo(() => {
    if (isGenerating && streamingText) {
      return [...messages, { role: "assistant", content: streamingText, _stream: true }];
    }
    return messages;
  }, [messages, isGenerating, streamingText]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [displayedMessages, activeId, isGenerating]);

  useEffect(() => {
    if (status === "error" && error) {
      toast.error("AI error", { description: error, duration: 7000 });
    }
  }, [status, error]);

  const send = async (text) => {
    const userText = (text ?? input).trim();
    if (!userText || isGenerating || isLoading) return;
    setInput("");

    // Lazy-load the model on first send
    if (status === "idle") {
      load();
      toast.info("Loading AI model", {
        description:
          "Downloading Qwen2.5-0.5B (~400 MB on first visit). Runs 100% in your browser — no server, no API key.",
        duration: 6000,
      });
    }

    const next = [...messages, { role: "user", content: userText }];
    setChats((c) => ({ ...c, [activeId]: next }));

    const system = buildSystemPrompt(
      activeId,
      activeId === "cover_letter" ? userText : null
    );

    // Build the message list for the model
    const modelMessages = [
      { role: "system", content: system },
      ...next.map((m) => ({ role: m.role, content: m.content })),
    ];

    const final = await generate(modelMessages, { maxTokens: 600 });
    setChats((c) => ({
      ...c,
      [activeId]: [...next, { role: "assistant", content: final || streamingText || "(no response)" }],
    }));
  };

  const reset = () => setChats((c) => ({ ...c, [activeId]: [] }));

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Progress UI helper
  const progressPct = useMemo(() => {
    if (!progress) return null;
    if (typeof progress.progress === "number") return Math.round(progress.progress);
    if (progress.total && progress.loaded)
      return Math.round((progress.loaded / progress.total) * 100);
    return null;
  }, [progress]);

  const statusBadge = (() => {
    if (status === "error") return { text: "error", cls: "border-red-500/40 bg-red-500/10 text-red-300" };
    if (isGenerating) return { text: "generating", cls: "border-[var(--jewel)]/40 bg-[var(--jewel)]/10 text-[var(--jewel)]" };
    if (isReady)
      return {
        text: `${device === "webgpu" ? "webgpu" : device === "wasm" ? "cpu/wasm" : "ready"} · live`,
        cls: "border-[var(--jewel)]/35 bg-[var(--jewel)]/[0.07] text-[var(--jewel)]",
      };
    if (isLoading) return { text: progressPct ? `loading ${progressPct}%` : "loading model", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" };
    return { text: "click send to load (~400MB)", cls: "border-white/15 bg-white/[0.03] text-muted-foreground" };
  })();

  return (
    <section
      id="ai"
      data-testid="ai-section"
      className="relative py-24 sm:py-32 border-t border-white/[0.06] overflow-hidden"
    >
      <div className="pointer-events-none absolute -left-32 top-32 h-[420px] w-[420px] rounded-full bg-[var(--jewel)] opacity-[0.06] blur-3xl" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[320px] w-[320px] rounded-full bg-[var(--jewel-2)] opacity-[0.07] blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="text-eyebrow flex items-center gap-2">
              <Sparkles size={12} /> // 03 · AI Lab
            </span>
            <h2 className="text-display mt-3 text-[2.6rem] sm:text-[3.6rem] leading-[0.95]">
              Skip the small talk. <br />
              <em className="italic text-[var(--jewel)]">Ask my AI</em>.
            </h2>
            <p className="mt-5 text-base text-muted-foreground max-w-xl leading-relaxed">
              Four AI tools trained on this portfolio. Powered by{" "}
              <span className="font-mono-ed text-foreground/90">Qwen2.5-0.5B-Instruct</span>{" "}
              running <strong className="text-foreground">100% in your browser</strong> via
              Transformers.js + WebGPU. No server, no API key.
            </p>
          </div>

          <div className="flex flex-col items-start sm:items-end gap-2">
            <span className={`pill inline-flex items-center gap-2 border ${statusBadge.cls}`} data-testid="ai-status-badge">
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current dot-live" />
              {statusBadge.text}
            </span>
            <span className="font-mono-ed text-[0.65rem] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Cpu size={11} /> on-device · free · private
            </span>
          </div>
        </div>

        {/* Model load progress bar */}
        {isLoading && (
          <div
            data-testid="ai-loading-bar"
            className="mb-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 font-mono-ed text-xs uppercase tracking-widest text-foreground/80">
                <Download size={13} className="animate-pulse text-[var(--jewel)]" />
                Loading {progress?.file ?? "model"}
              </span>
              <span className="font-mono-ed text-xs text-muted-foreground">
                {progressPct !== null ? `${progressPct}%` : "preparing…"}
              </span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full bg-[var(--jewel)] transition-all duration-300"
                style={{ width: `${progressPct ?? 8}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              First load only — model is cached after this.
            </p>
          </div>
        )}

        {/* feature tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" role="tablist">
          {AI_FEATURES.map((f) => {
            const Icon = FEATURE_ICONS[f.id];
            const isActive = f.id === activeId;
            return (
              <button
                key={f.id}
                role="tab"
                aria-selected={isActive}
                data-testid={`ai-tab-${f.id}`}
                onClick={() => setActiveId(f.id)}
                className={`relative group flex flex-col items-start text-left rounded-2xl border p-4 transition-all duration-300 ${
                  isActive
                    ? "border-[var(--jewel)] bg-[var(--jewel)]/[0.06]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/30"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`h-8 w-8 rounded-full grid place-items-center ${
                      isActive
                        ? "bg-[var(--jewel)] text-black"
                        : "bg-white/[0.04] text-foreground/70 group-hover:text-foreground"
                    }`}
                  >
                    <Icon size={15} />
                  </div>
                  <span className="font-mono-ed text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                    {f.short}
                  </span>
                </div>
                <p
                  className={`font-serif text-lg leading-tight ${
                    isActive ? "text-foreground" : "text-foreground/85"
                  }`}
                >
                  {f.label}
                </p>
              </button>
            );
          })}
        </div>

        {/* chat panel */}
        <div
          data-testid="ai-chat-panel"
          className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.005] overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-2 w-2 rounded-full bg-[var(--jewel)] dot-live" />
              <p className="font-mono-ed text-xs uppercase tracking-widest text-foreground/90 truncate">
                {active.label}
              </p>
              <span className="hidden sm:inline text-muted-foreground/60 text-xs">·</span>
              <p className="hidden sm:block text-xs text-muted-foreground truncate">{active.desc}</p>
            </div>
            <button
              onClick={reset}
              data-testid="ai-reset"
              className="flex items-center gap-1.5 font-mono-ed text-[0.7rem] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
            >
              <RotateCcw size={12} /> reset
            </button>
          </div>

          <div
            ref={scrollRef}
            className="chat-scroll relative min-h-[340px] max-h-[500px] overflow-y-auto p-5 sm:p-6 space-y-4"
            data-testid="ai-messages"
          >
            {displayedMessages.length === 0 && !isGenerating && !isLoading && (
              <div className="py-6">
                <p className="font-serif text-2xl sm:text-3xl text-foreground/85 leading-snug max-w-2xl">
                  {active.desc}
                </p>
                <div className="mt-6">
                  <p className="text-eyebrow mb-3">try one of these</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS[activeId].map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid={`ai-suggestion-${s.slice(0, 24)}`}
                        className="text-left rounded-full border border-white/15 bg-white/[0.02] px-4 py-2 text-sm text-foreground/80 hover:border-[var(--jewel)] hover:text-foreground transition-all"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                {status === "idle" && (
                  <p className="mt-6 text-xs text-muted-foreground flex items-center gap-1.5">
                    <Zap size={12} className="text-[var(--jewel)]" />
                    First message will download the ~400 MB Qwen model into your browser. Cached after.
                  </p>
                )}
              </div>
            )}

            {displayedMessages.map((m, i) => (
              <Message key={i} role={m.role} content={m.content} streaming={m._stream} />
            ))}

            {isGenerating && !streamingText && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="ai-thinking">
                <Loader2 size={14} className="animate-spin" /> Thinking…
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 sm:p-4 bg-black/30">
            <div className="flex items-end gap-2">
              <textarea
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={active.placeholder}
                data-testid="ai-input"
                className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none px-3 py-2.5 max-h-40"
                disabled={isGenerating}
              />
              <button
                onClick={() => send()}
                disabled={isGenerating || !input.trim()}
                data-testid="ai-send"
                className="inline-flex items-center gap-2 btn-ink px-4 py-2.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isGenerating || isLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                Send
              </button>
            </div>
          </div>
        </div>

        <p className="mt-3 font-mono-ed text-[0.7rem] uppercase tracking-widest text-muted-foreground/70">
          // Runs locally via Transformers.js · No tokens sent to any server.
        </p>
      </div>
    </section>
  );
}

function Message({ role, content, streaming }) {
  if (role === "user") {
    return (
      <div className="flex justify-end reveal" data-testid="ai-msg-user">
        <div className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-3 text-sm bg-[var(--jewel)] text-black leading-relaxed whitespace-pre-wrap">
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 reveal" data-testid="ai-msg-assistant">
      <div className="h-8 w-8 shrink-0 rounded-full grid place-items-center bg-white/[0.05] border border-white/10">
        <Sparkles size={14} className="text-[var(--jewel)]" />
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-white/10 bg-white/[0.025] px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
        {content}
        {streaming && (
          <span className="blink ml-0.5 inline-block h-[1em] w-[2px] translate-y-[0.18em] bg-[var(--jewel)]" />
        )}
      </div>
    </div>
  );
}
